import React, { useEffect, useMemo } from "react";
import "./Scoreboard.css";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { useDispatch, useSelector } from "react-redux";
import { getAIScoreboardCourses } from "Redux/CourseDetails";
import { syncUser } from "Redux/UserDetails";
import { useMsal } from "@azure/msal-react";
import {
  applyScoreboardRules,
  checkProgramCompleted,
} from "utils/foundationUnlock";
import {
  computeScore,
  computeLevel,
  computePoints,
  computeNextCategories,
  sumProgramPoints,
} from "utils/scoreboard";
import ProgressGauge from "./ProgressGauge";

const ScoreboardContent = () => {
  const dispatch = useDispatch();
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const enterpriseId = activeAccount?.idTokenClaims?.enterpriseID;
  const name = activeAccount?.idTokenClaims?.name;
  const userLevelDes = activeAccount?.idTokenClaims?.jobFamilyDescription;
  const role = activeAccount?.idTokenClaims?.jobTitle;
  const userData = useSelector((state) => state.userDetails.userData?.[0]);
  const isEmpty = (value) =>
    value === null || value === undefined || value === "" || value === "NULL";
  const level = useMemo(() => {
    return isEmpty(userLevelDes) ? userData?.level : userLevelDes;
  }, [userLevelDes, userData?.level]);
  const { AIScoreboardCourses = [] } = useSelector(
    (state) => state.courseDetails,
  );
  const {
    scoreboardCoursesGroupedByCategory,
    totalPointsPerCategory,
    programPointsPerCategory,
  } = useMemo(() => {
    const result = applyScoreboardRules({
      AIScoreboardCourses,
      userData,
    });

    checkProgramCompleted(result.scoreboardCoursesGroupedByCategory);

    return result;
  }, [AIScoreboardCourses, userData]);

  const {
    score,
    percentile,
    currentLevel,
    nextLevel,
    completedPointsPerCategory,
    nextCategories,
  } = useMemo(() => {
    const completedPointsPerCategory = computePoints(
      scoreboardCoursesGroupedByCategory,
      programPointsPerCategory,
    );

    const score = computeScore(
      scoreboardCoursesGroupedByCategory,
      totalPointsPerCategory,
      completedPointsPerCategory,
    );
    const percentile = Math.min(
      100,
      Math.max(0, Math.round((score / 500) * 100)),
    );
    const { currentLevel, nextLevel } = computeLevel(score);

    const nextCategories = computeNextCategories({
      scoreboardCoursesGroupedByCategory,
      totalPointsPerCategory,
      completedPointsPerCategory,
      limit: 4,
    });

    return {
      score,
      percentile,
      currentLevel,
      nextLevel,
      completedPointsPerCategory,
      nextCategories,
    };
  }, [
    scoreboardCoursesGroupedByCategory,
    totalPointsPerCategory,
    programPointsPerCategory,
  ]);

  useEffect(() => {
    if (!enterpriseId) return;

    dispatch(syncUser(enterpriseId, name, level, role));
    dispatch(getAIScoreboardCourses({ enterpriseId }));
  }, [enterpriseId, dispatch]);

  return (
    <>
      <div className="few-pages-banner-wrap">
        <img
          src={
            process.env.PUBLIC_URL + "/Assets/Scoreboard/scoreboard-banner.png"
          }
          // src={urls["bannerIgniteTalks"]}
          alt="Ignite banner"
          className="banner-img"
        />
        <div className="scoreboard-page-banner-box">
          <div className="scoreboard-banner-wrapper">
            <div className="scoreboard-banner-content">
              {/* <h1 className="scoreboard-banner-main-head">Hi {user.name}...</h1> */}
              <h1 className="scoreboard-banner-main-head">
                Here is your AI scoreboard
              </h1>
              <div className="banner-score-text">
                <div className="banner-sub-div">
                  <p>Your Score</p>
                  <h1>{score}</h1>
                  <p>out of 600</p>
                </div>
                <div className="banner-sub-div">
                  <p>Your Level</p>
                  <h1>{currentLevel}</h1>
                  <p>Next {nextLevel}</p>
                </div>
              </div>
            </div>
            <div className="banner-right">
              <ProgressGauge score={score} />
            </div>
          </div>
        </div>
      </div>
      <div className="scoreboard-milestones-wrapper">
        <div className="scoreboard-milestones">
          <div className="milestones-left">
            <img
              src={
                process.env.PUBLIC_URL +
                "/Assets/Scoreboard/milestone-icon-img.png"
              }
              // src={urls["bannerIgniteTalks"]}
              alt="Milestone Icon"
              className="milestone-img-icon"
            />
          </div>
          <div className="milestones-right">
            <h3>
              {nextLevel !== ""
                ? `Your are now ${currentLevel} earn your next milestone`
                : `Your are now ${currentLevel} and have earned all milestones`}
            </h3>
            <p>
              {nextLevel !== ""
                ? `Complete ${currentLevel} to earn ${nextLevel}`
                : `Completed`}
            </p>
            <span>{percentile}% complete</span>
            <span className="AI-powered">{currentLevel}</span>
            {/* <progress max={100} value={20}>20%</progress> */}
            <div className="progress-container">
              <div
                className="progress-bar"
                style={{ width: `${percentile}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>
      <div className="score-details-section">
        <div className="score-breakdown">
          <div className="breakdown-heading">
            <div className="heading-left">
              <h3>Module Score breakdown</h3>
              <p>Earned points learning module - {userData?.role} role</p>
            </div>
            <div className="heading-right">
              <TrendingUpIcon />
            </div>
          </div>
          <div className="breakdown-content">
            {Array.from(scoreboardCoursesGroupedByCategory.entries()).map(
              ([categoryName]) => {
                const totalPoints =
                  Number(totalPointsPerCategory.get(categoryName)) || 0;

                const categoryProgramPoints =
                  programPointsPerCategory.get(categoryName) || {};

                const completedProgramPoints =
                  completedPointsPerCategory.get(categoryName) || {};

                const completedPoints = Object.values(
                  completedProgramPoints,
                ).reduce((total, points) => {
                  const numericPoints = Number(points);

                  return (
                    total + (Number.isFinite(numericPoints) ? numericPoints : 0)
                  );
                }, 0);

                const categoryPrograms = Object.entries(categoryProgramPoints);

                const isCategoryCompleted =
                  totalPoints > 0 && completedPoints >= totalPoints;

                return (
                  <React.Fragment key={categoryName}>
                    <div className="score-cards">
                      <div className="score-cards-details">
                        <h4>{categoryName}</h4>
                      </div>

                      <div className="score-cards-pts">
                        <p
                          id="breakdown-points"
                          style={{
                            backgroundColor: isCategoryCompleted
                              ? "black"
                              : "#B1B1B1",
                          }}
                        >
                          {totalPoints} PT
                        </p>
                      </div>
                    </div>

                    <div className="module-program-dropdown">
                      {categoryPrograms.length > 0 ? (
                        categoryPrograms.map(
                          ([programName, assignedPoints]) => {
                            const earnedPointsValue = Number(
                              completedProgramPoints[programName],
                            );

                            const earnedPoints = Number.isFinite(
                              earnedPointsValue,
                            )
                              ? earnedPointsValue
                              : 0;

                            const assignedPointsValue = Number(assignedPoints);

                            const numericAssignedPoints = Number.isFinite(
                              assignedPointsValue,
                            )
                              ? assignedPointsValue
                              : 0;

                            const isProgramCompleted =
                              numericAssignedPoints > 0 &&
                              earnedPoints >= numericAssignedPoints;

                            return (
                              <div
                                className="module-program-item"
                                key={`${categoryName}-${programName}`}
                              >
                                <span className="module-program-name">
                                  {programName}
                                </span>

                                <span
                                  className={`module-program-points ${
                                    isProgramCompleted
                                      ? "module-program-completed"
                                      : ""
                                  }`}
                                />

                                <span className="divider" />
                              </div>
                            );
                          },
                        )
                      ) : (
                        <div className="module-program-empty">
                          No programs available
                        </div>
                      )}
                    </div>
                  </React.Fragment>
                );
              },
            )}
          </div>
        </div>
        <div className="next-step-wrapper">
          <div className="next-score-step">
            <div className="next-heading">
              <h3>Next Step</h3>
              <p>Complete below modules to earn AI points</p>
            </div>
            <div className="next-content">
              {nextCategories.map(
                ({ categoryName, completedPoints, totalPoints }) => (
                  <div
                    className="next-scorecards"
                    key={categoryName}
                    style={{
                      border: "1px solid #cc66ff",
                      borderLeft: "3px solid #cc66ff",
                    }}
                  >
                    <h4>{categoryName}</h4>

                    <span id="next-pts" style={{ color: "#a100ff" }}>
                      {completedPoints}
                    </span>

                    <span> / {totalPoints} Points</span>
                  </div>
                ),
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ScoreboardContent;
